import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Style from '../styles/ServerStatus.module.css';

const apiBase = process.env.REACT_APP_API_BASE || '';
const categories = ['Concrete', 'Paint', 'Decks/Fences', 'Roofing', 'Stone Patio', 'Block Foundation'];

function formatUptime(seconds = 0) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${days}d ${hours}h ${minutes}m`;
}

function readImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read image file.'));
    reader.readAsDataURL(file);
  });
}

function ServerStatus() {
  const [token, setToken] = useState(() => localStorage.getItem('dinoStatusToken') || '');
  const [activeTab, setActiveTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(categories[0]);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadResults, setUploadResults] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const loadStatus = async () => {
    if (!token) {
      setIsUnlocked(false);
      setError('Enter the owner password to view the dashboard.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [statusResponse, logsResponse, analyticsResponse] = await Promise.all([
        fetch(`${apiBase}/api/admin/status`, { headers: authHeaders }),
        fetch(`${apiBase}/api/admin/logs?limit=50`, { headers: authHeaders }),
        fetch(`${apiBase}/api/admin/analytics`, { headers: authHeaders }),
      ]);

      if (!statusResponse.ok || !logsResponse.ok) {
        throw new Error('Unable to load dashboard. Check the token and API URL.');
      }

      const statusData = await statusResponse.json();
      const logsData = await logsResponse.json();
      const analyticsData = analyticsResponse.ok ? await analyticsResponse.json() : null;

      setStatus(statusData);
      setLogs(logsData.logs || []);
      setAnalytics(analyticsData);
      setIsUnlocked(true);
      localStorage.setItem('dinoStatusToken', token);
    } catch (requestError) {
      setIsUnlocked(false);
      setStatus(null);
      setLogs([]);
      setAnalytics(null);
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenChange = (event) => {
    setToken(event.target.value);
    setIsUnlocked(false);
    setStatus(null);
    setLogs([]);
    setAnalytics(null);
    setUploadQueue([]);
    setUploadResults([]);
    setUploadMessage('');
    setError('');
    localStorage.removeItem('dinoStatusToken');
  };

  const handleUploadFileChange = async (event) => {
    const files = Array.from(event.target.files || []);

    setUploadResults([]);
    setUploadMessage('');

    if (files.length === 0) {
      return;
    }

    const validFiles = files.filter((file) => file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024);

    if (validFiles.length !== files.length) {
      setUploadMessage('Some files were skipped. Images must be under 10 MB.');
    }

    if (validFiles.length === 0) {
      return;
    }

    const nextImages = await Promise.all(validFiles.map(async (file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      title: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' '),
      category: uploadCategory,
      src: await readImageFile(file),
      size: file.size,
    })));

    setUploadQueue((currentQueue) => [...currentQueue, ...nextImages]);
    event.target.value = '';
  };

  const handleQueuedTitleChange = (id, title) => {
    setUploadQueue((currentQueue) => currentQueue.map((image) => (
      image.id === id ? { ...image, title } : image
    )));
  };

  const handleQueuedCategoryChange = (id, category) => {
    setUploadQueue((currentQueue) => currentQueue.map((image) => (
      image.id === id ? { ...image, category } : image
    )));
  };

  const handleQueuedImageRemove = (id) => {
    setUploadQueue((currentQueue) => currentQueue.filter((image) => image.id !== id));
  };

  const handleGalleryUpload = async (event) => {
    event.preventDefault();

    if (!isUnlocked) {
      setUploadMessage('Unlock the dashboard before uploading.');
      return;
    }

    if (uploadQueue.length === 0) {
      setUploadMessage('Choose one or more images before uploading.');
      return;
    }

    setIsUploading(true);
    setUploadMessage('');
    setUploadResults([]);

    try {
      const results = [];

      for (const queuedImage of uploadQueue) {
        const uploadResponse = await fetch(`${apiBase}/api/admin/gallery/upload`, {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image: queuedImage.src,
            title: queuedImage.title,
            category: queuedImage.category,
          }),
        });

        const uploadData = await uploadResponse.json();

        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || uploadData.message || `Upload failed for ${queuedImage.name}.`);
        }

        results.push(uploadData);
      }

      setUploadResults(results);
      setUploadMessage(`${results.length} image${results.length === 1 ? '' : 's'} uploaded to Cloudinary.`);
      setUploadQueue([]);
      await loadStatus();
    } catch (uploadError) {
      setUploadMessage(uploadError.message);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className={Style.Page}>
      <Helmet>
        <title>Owner Dashboard - Dino Construction</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <section className={Style.Hero}>
        <div>
          <p>Owner tools</p>
          <h1>Dino dashboard</h1>
          <span>Check the estimate API, review recent logs, and upload gallery images to Cloudinary.</span>
        </div>
      </section>

      <section className={Style.Panel}>
        <div className={Style.TokenForm}>
          <label htmlFor="statusToken">Owner password</label>
          <div>
            <input
              id="statusToken"
              type="password"
              value={token}
              onChange={handleTokenChange}
              placeholder="Enter owner password"
            />
            <button type="button" onClick={loadStatus} disabled={isLoading}>
              {isLoading ? 'Checking...' : 'Unlock Dashboard'}
            </button>
          </div>
          {error && <p className={Style.Error}>{error}</p>}
        </div>

        <div className={Style.Tabs} aria-label="Dashboard sections">
          <button className={activeTab === 'status' ? Style.activeTab : ''} type="button" onClick={() => setActiveTab('status')}>Status</button>
          <button className={activeTab === 'analytics' ? Style.activeTab : ''} type="button" onClick={() => setActiveTab('analytics')}>Analytics</button>
          <button className={activeTab === 'upload' ? Style.activeTab : ''} type="button" onClick={() => setActiveTab('upload')}>Gallery Upload</button>
          <button className={activeTab === 'logs' ? Style.activeTab : ''} type="button" onClick={() => setActiveTab('logs')}>Logs</button>
        </div>

        {activeTab === 'status' && (
          <>
            {status ? (
              <>
                <div className={Style.StatusGrid}>
                  <div>
                    <span>API</span>
                    <strong>{status.status === 'ok' ? 'Running' : 'Check server'}</strong>
                  </div>
                  <div>
                    <span>Uptime</span>
                    <strong>{formatUptime(status.uptimeSeconds)}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{status.emailConfigured ? 'Configured' : 'Missing setup'}</strong>
                  </div>
                  <div>
                    <span>Cloudinary</span>
                    <strong>{status.cloudinaryConfigured ? 'Ready' : 'Missing setup'}</strong>
                  </div>
                  <div>
                    <span>Gallery cloud</span>
                    <strong>{status.cloudinaryGalleryUsage?.imageCount || 0} images</strong>
                  </div>
                </div>

                <div className={Style.Details}>
                  <p><strong>Started:</strong> {new Date(status.startedAt).toLocaleString()}</p>
                  <p><strong>Node:</strong> {status.nodeVersion}</p>
                  <p><strong>From:</strong> {status.mailFrom}</p>
                  <p><strong>To:</strong> {status.mailTo}</p>
                  <p><strong>Rate limit:</strong> {status.rateLimit.maxRequests} requests per hour</p>
                  <p><strong>Active clients:</strong> {status.rateLimit.activeClients}</p>
                  <p><strong>Dino gallery storage:</strong> {status.cloudinaryGalleryUsage?.megabytes || 0} MB</p>
                  <p><strong>Cloudinary folder:</strong> dino-construction/gallery</p>
                </div>
              </>
            ) : (
              <div className={Style.EmptyPanel}>
                <h2>Dashboard locked</h2>
                <p>Enter the owner password and unlock the dashboard to load live server status.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'analytics' && (
          analytics ? (
            <div className={Style.AnalyticsPanel}>
              <div className={Style.AnalyticsHeader}>
                <div>
                  <p>Website traffic</p>
                  <h2>Last 30 days</h2>
                </div>
                <button type="button" onClick={loadStatus} disabled={isLoading}>Refresh</button>
              </div>

              <div className={Style.AnalyticsGrid}>
                <div><span>Visits today</span><strong>{analytics.totals.todayVisits}</strong></div>
                <div><span>Visitors today</span><strong>{analytics.totals.todayVisitors}</strong></div>
                <div><span>30-day visits</span><strong>{analytics.totals.last30Visits}</strong></div>
                <div><span>30-day visitors</span><strong>{analytics.totals.last30Visitors}</strong></div>
                <div><span>All page views</span><strong>{analytics.totals.allTimePageViews}</strong></div>
              </div>

              <section className={Style.TrafficChart}>
                <div className={Style.SectionHeading}>
                  <h3>Daily traffic</h3>
                  <span>Page views for the last 14 days</span>
                </div>
                <div className={Style.ChartScroll}>
                  <div className={Style.ChartBars}>
                    {analytics.daily.map((day) => {
                      const maxViews = Math.max(...analytics.daily.map((item) => item.pageViews), 1);
                      const barHeight = Math.max((day.pageViews / maxViews) * 100, day.pageViews > 0 ? 8 : 2);

                      return (
                        <div className={Style.ChartDay} key={day.date} title={`${day.pageViews} page views, ${day.visits} visits`}>
                          <strong>{day.pageViews}</strong>
                          <div><span style={{ height: `${barHeight}%` }} /></div>
                          <small>{new Date(`${day.date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</small>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

              <div className={Style.AnalyticsLists}>
                <section>
                  <div className={Style.SectionHeading}>
                    <h3>Popular pages</h3>
                    <span>Last 30 days</span>
                  </div>
                  {analytics.topPages.length > 0 ? analytics.topPages.map((page) => (
                    <div className={Style.AnalyticsRow} key={page.name}>
                      <span>{page.name}</span>
                      <strong>{page.count}</strong>
                    </div>
                  )) : <p className={Style.AnalyticsEmpty}>Traffic will appear after the updated website goes live.</p>}
                </section>

                <section>
                  <div className={Style.SectionHeading}>
                    <h3>Traffic sources</h3>
                    <span>Last 30 days</span>
                  </div>
                  {analytics.topReferrers.length > 0 ? analytics.topReferrers.map((referrer) => (
                    <div className={Style.AnalyticsRow} key={referrer.name}>
                      <span>{referrer.name}</span>
                      <strong>{referrer.count}</strong>
                    </div>
                  )) : <p className={Style.AnalyticsEmpty}>No referral sources recorded yet.</p>}
                </section>
              </div>

              <p className={Style.PrivacyNote}>Tracking began {new Date(analytics.trackedSince).toLocaleDateString()}. Counts exclude the owner dashboard, common bots, and visitors who enable Do Not Track. Raw IP addresses are not stored.</p>
            </div>
          ) : (
            <div className={Style.EmptyPanel}>
              <h2>No analytics loaded</h2>
              <p>Unlock or refresh the dashboard after the updated API is running.</p>
            </div>
          )
        )}

        {activeTab === 'upload' && (
          isUnlocked ? (
            <div className={Style.UploadPanel}>
            <form className={Style.UploadForm} onSubmit={handleGalleryUpload}>
              <div>
                <label htmlFor="galleryCategory">Category</label>
                <select
                  id="galleryCategory"
                  value={uploadCategory}
                  onChange={(event) => setUploadCategory(event.target.value)}
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div className={Style.FileField}>
                <label htmlFor="galleryImage">Project images</label>
                <input id="galleryImage" type="file" accept="image/*" multiple onChange={handleUploadFileChange} />
              </div>
              <button type="submit" disabled={isUploading}>
                {isUploading ? 'Uploading...' : `Upload ${uploadQueue.length || ''} Image${uploadQueue.length === 1 ? '' : 's'}`}
              </button>
              {uploadMessage && <p className={uploadResults.length > 0 ? Style.Success : Style.Error}>{uploadMessage}</p>}
            </form>

            <div className={Style.UploadPreview}>
              {uploadQueue.length > 0 ? (
                <div className={Style.UploadQueue}>
                  {uploadQueue.map((image, index) => (
                    <article key={image.id}>
                      <img src={image.src} alt={`Queued upload ${index + 1}`} />
                      <div>
                        <label htmlFor={`upload-title-${image.id}`}>Title</label>
                        <input
                          id={`upload-title-${image.id}`}
                          type="text"
                          value={image.title}
                          onChange={(event) => handleQueuedTitleChange(image.id, event.target.value)}
                        />
                        <label htmlFor={`upload-category-${image.id}`}>Category</label>
                        <select
                          id={`upload-category-${image.id}`}
                          value={image.category}
                          onChange={(event) => handleQueuedCategoryChange(image.id, event.target.value)}
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                        <span>{image.name}</span>
                        <button type="button" onClick={() => handleQueuedImageRemove(image.id)}>Remove</button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div>
                  <span>No images selected</span>
                </div>
              )}
            </div>

            {uploadResults.length > 0 && (
              <div className={Style.UploadResult}>
                <h2>Uploaded Cloudinary images</h2>
                {uploadResults.map((result, index) => (
                  <p key={result.publicId}>
                    <strong>{index + 1}.</strong> {result.publicId} | {result.width} x {result.height} | <a href={result.optimizedUrl} target="_blank" rel="noreferrer">Open</a>
                  </p>
                ))}
              </div>
            )}
            </div>
          ) : (
            <div className={Style.EmptyPanel}>
              <h2>Gallery upload locked</h2>
              <p>Enter the owner password and unlock the dashboard before selecting or uploading images.</p>
            </div>
          )
        )}

        {activeTab === 'logs' && (
          <div className={Style.Logs}>
            <div className={Style.LogHeader}>
              <h2>Recent logs</h2>
              <button type="button" onClick={loadStatus} disabled={isLoading}>Refresh</button>
            </div>
            {logs.length > 0 ? (
              <div className={Style.LogList}>
                {logs.map((log) => (
                  <article className={Style.LogRow} key={log.id}>
                    <span className={Style[log.level] || Style.info}>{log.level}</span>
                    <div>
                      <strong>{log.message}</strong>
                      <p>{new Date(log.timestamp).toLocaleString()}</p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <pre>{JSON.stringify(log.details, null, 2)}</pre>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className={Style.EmptyPanel}>
                <h2>No logs loaded</h2>
                <p>Unlock the dashboard or refresh logs to view recent server activity.</p>
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

export default ServerStatus;
